import { useMemo, useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { useStatusTracksByConfiguration } from '@/features/contract/statuses/hooks'
import { useDocumentTypes } from '@/features/admin/document-types/hooks'
import { useCreateRequirement } from '@/features/contract/gating/hooks'
import { useFormSchema } from '@/features/admin/forms/hooks'
import type { FinancingMetric, RequirementKind } from '@/features/contract/gating/types'
import { FINANCING_METRIC_LABEL, FINANCING_METRIC_NUMERIC } from '@/features/contract/gating/types'
import { NumericInputBase } from '@/features/contract/components/field-renderer/inputs/NumericInputBase'
import { formatError } from './formatError'

export function RequirementFormDialog({
  configurationCode, stageCode, stageLabel, groupNo, onClose,
}: {
  configurationCode: string
  stageCode: string
  stageLabel: string
  groupNo: number
  onClose: () => void
}) {
  const tracks = useStatusTracksByConfiguration(configurationCode, true)
  const documentTypes = useDocumentTypes(configurationCode, true)
  const contractSchema = useFormSchema('contract')
  const create = useCreateRequirement(configurationCode)
  const [kind, setKind] = useState<RequirementKind>('STATUS')
  const [trackId, setTrackId] = useState<number | ''>('')
  const [requiredValueId, setRequiredValueId] = useState<number | ''>('')
  const [documentTypeId, setDocumentTypeId] = useState<number | ''>('')
  const [fieldCode, setFieldCode] = useState<string | ''>('')
  const [financingMetric, setFinancingMetric] = useState<FinancingMetric>('PAID_PERCENT')
  const [financingValue, setFinancingValue] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const selectedTrack = tracks.data?.find(t => t.id === trackId)
  const verifyEnabledFields = useMemo(
    () => (contractSchema.data?.fields ?? []).filter(f => f.verificationModes.length > 0 && f.isActive),
    [contractSchema.data],
  )

  const submit = () => {
    setError(null)
    if (kind === 'STATUS') {
      if (!trackId || !requiredValueId) { setError('Wybierz tor i wartość.'); return }
      create.mutate(
        {
          stageCode,
          kind: 'STATUS',
          trackId: Number(trackId),
          requiredValueId: Number(requiredValueId),
          groupNo,
          sortOrder: 100,
        },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else if (kind === 'DOCUMENT') {
      if (!documentTypeId) { setError('Wybierz typ dokumentu.'); return }
      create.mutate(
        {
          stageCode,
          kind: 'DOCUMENT',
          documentTypeId: Number(documentTypeId),
          groupNo,
          sortOrder: 100,
        },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else if (kind === 'FIELD_VERIFIED') {
      if (!fieldCode) { setError('Wybierz pole.'); return }
      create.mutate(
        {
          stageCode,
          kind: 'FIELD_VERIFIED',
          fieldCode,
          groupNo,
          sortOrder: 100,
        },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else {
      // FINANCING
      if (FINANCING_METRIC_NUMERIC[financingMetric] && (financingValue == null || financingValue <= 0)) {
        setError('Podaj minimalną wartość (%).'); return
      }
      create.mutate(
        {
          stageCode,
          kind: 'FINANCING',
          financingMetric,
          financingValue: FINANCING_METRIC_NUMERIC[financingMetric] ? financingValue : null,
          groupNo,
          sortOrder: 100,
        },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    }
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[460px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Nowy warunek</Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            Etap: <strong>{stageLabel}</strong> · Grupa <strong>{groupNo}</strong>
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <div className="grid grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => setKind('STATUS')}
                className={`rounded border px-3 py-2 text-left ${kind === 'STATUS' ? 'border-sky-500 bg-sky-50' : 'border-slate-300'}`}
              >
                <div className="font-medium text-xs uppercase">Status</div>
                <div className="text-[11px] text-slate-500">Tor statusu = wartość</div>
              </button>
              <button
                type="button"
                onClick={() => setKind('DOCUMENT')}
                className={`rounded border px-3 py-2 text-left ${kind === 'DOCUMENT' ? 'border-amber-500 bg-amber-50' : 'border-slate-300'}`}
              >
                <div className="font-medium text-xs uppercase">Dokument</div>
                <div className="text-[11px] text-slate-500">Załącznik wybranego typu</div>
              </button>
              <button
                type="button"
                onClick={() => setKind('FIELD_VERIFIED')}
                className={`rounded border px-3 py-2 text-left ${kind === 'FIELD_VERIFIED' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300'}`}
              >
                <div className="font-medium text-xs uppercase">Weryfikacja</div>
                <div className="text-[11px] text-slate-500">Pole z aktywnym ptaszkiem</div>
              </button>
              <button
                type="button"
                onClick={() => setKind('FINANCING')}
                className={`rounded border px-3 py-2 text-left ${kind === 'FINANCING' ? 'border-rose-500 bg-rose-50' : 'border-slate-300'}`}
              >
                <div className="font-medium text-xs uppercase">Finanse</div>
                <div className="text-[11px] text-slate-500">Warunek finansowy</div>
              </button>
            </div>

            {kind === 'STATUS' && (
              <>
                <label className="grid gap-1">
                  <span className="text-xs uppercase text-slate-500">Tor</span>
                  <Combobox<number>
                    value={trackId === '' ? null : trackId}
                    onChange={v => { setTrackId(v ?? ''); setRequiredValueId('') }}
                    options={(tracks.data ?? []).map(t => ({ value: t.id, label: t.labelPl }))}
                    emptyOptionLabel="- wybierz -"
                    placeholder="Wybierz tor..."
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-xs uppercase text-slate-500">Dopuszczalna wartość</span>
                  <Combobox<number>
                    value={requiredValueId === '' ? null : requiredValueId}
                    onChange={v => setRequiredValueId(v ?? '')}
                    options={(selectedTrack?.values ?? []).map(v => ({ value: v.id, label: v.labelPl }))}
                    emptyOptionLabel="- wybierz -"
                    placeholder="Wybierz wartość..."
                    disabled={!selectedTrack}
                  />
                </label>
              </>
            )}

            {kind === 'DOCUMENT' && (
              <label className="grid gap-1">
                <span className="text-xs uppercase text-slate-500">Wymagany typ dokumentu</span>
                <Combobox<number>
                  value={documentTypeId === '' ? null : documentTypeId}
                  onChange={v => setDocumentTypeId(v ?? '')}
                  options={(documentTypes.data ?? []).map(t => ({ value: t.id, label: t.labelPl }))}
                  emptyOptionLabel="- wybierz -"
                  placeholder="Wybierz typ dokumentu..."
                />
                <span className="text-[10px] text-slate-400">
                  Bramka otwiera się gdy załącznik wybranego typu istnieje na umowie (≥ 1).
                </span>
              </label>
            )}

            {kind === 'FIELD_VERIFIED' && (
              <label className="grid gap-1">
                <span className="text-xs uppercase text-slate-500">Wymagana weryfikacja pola</span>
                <Combobox<string>
                  value={fieldCode === '' ? null : fieldCode}
                  onChange={v => setFieldCode(v ?? '')}
                  options={verifyEnabledFields.map(f => ({ value: f.fieldCode, label: f.labelPl }))}
                  emptyOptionLabel="- wybierz -"
                  placeholder="Wybierz pole..."
                />
                <span className="text-[10px] text-slate-400">
                  Tylko pola z włączoną weryfikacją (FormBuilder → Weryfikacja).
                  Bramka otwiera się, gdy istnieje aktywna weryfikacja danego pola.
                </span>
              </label>
            )}

            {kind === 'FINANCING' && (
              <>
                <label className="grid gap-1">
                  <span className="text-xs uppercase text-slate-500">Metryka</span>
                  <select
                    value={financingMetric}
                    onChange={e => setFinancingMetric(e.target.value as FinancingMetric)}
                    className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
                  >
                    {(Object.keys(FINANCING_METRIC_LABEL) as FinancingMetric[]).map(m => (
                      <option key={m} value={m}>{FINANCING_METRIC_LABEL[m]}</option>
                    ))}
                  </select>
                </label>
                {FINANCING_METRIC_NUMERIC[financingMetric] && (
                  <label className="grid gap-1">
                    <span className="text-xs uppercase text-slate-500">Minimalna wartość</span>
                    <NumericInputBase
                      value={financingValue}
                      onChange={v => setFinancingValue(v as number | null)}
                      decimals={2}
                      suffix="%"
                    />
                  </label>
                )}
                <span className="text-[10px] text-slate-400">
                  Bramka otwiera się gdy warunek finansowy umowy jest spełniony (operator ≥ dla metryk procentowych).
                </span>
              </>
            )}

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={create.isPending}>Anuluj</button>
              <button type="button" onClick={submit} className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50" disabled={create.isPending}>
                {create.isPending ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
